/**
 * Hook de fetch batché pour le builder de rapports Orion (Phase 2a).
 *
 * Porte la structure du batching de Metrix (une requête groupée, isolation
 * d'erreur par widget) mais s'adapte à la shape exacte de l'endpoint Orion
 * `POST /api/query/batch` (voir spec Ph2a) — ne réutilise PAS la shape
 * `QueryResponse` de Metrix.
 */

import { useEffect, useMemo, useState } from 'react';
import type { Widget, WidgetConfig, PeriodValue } from './schemas';

/** Une ligne de résultat de requête : nom de champ -> valeur. */
export type Row = Record<string, string | number>;

/** Résultat de requête pour un widget (shape renvoyée par /api/query/batch). */
export interface WidgetQueryResult {
  current: Row[];
  previous: Row[] | null;
  meta: unknown;
  /** Sous-lignes du regroupement hiérarchique (widget table), `null` si non configuré ou en échec. */
  subRows?: Row[] | null;
  /** Message d'erreur si la requête sous-lignes a échoué (distinct de "non configuré"). */
  subRowsError?: string | null;
}

interface QueryItem {
  id: string;
  data_source_id: number;
  metrics: string[];
  dimensions: string[];
  filters: WidgetConfig['filters'];
  filters_logic?: 'and' | 'or';
  start: string;
  end: string;
  granularity?: string;
  compare?: boolean;
  compare_mode?: string;
  compare_start?: string;
  compare_end?: string;
  location_type?: string;
}

interface BatchResponseItem {
  data: WidgetQueryResult | null;
  error: { message: string } | null;
}

interface BatchResponse {
  success: boolean;
  data: Record<string, BatchResponseItem>;
}

/** Période au niveau rapport = la valeur du PeriodPicker (start/end + objet compare). */
export type ReportPeriod = PeriodValue;

export interface UseWidgetDataResult {
  dataByWidgetId: Record<string, WidgetQueryResult>;
  loading: boolean;
  errorByWidgetId: Record<string, string>;
}

/** Un widget ne génère une query que s'il a un type interrogeable et une source configurée. */
function isQueryableWidget(widget: Widget): boolean {
  if (widget.type === 'divider') return false;
  if (!widget.config?.data_source_id) return false;
  return true;
}

function buildQueryItem(widget: Widget, period: ReportPeriod): QueryItem {
  const config = widget.config;
  const useCustomPeriod =
    config.period?.mode === 'custom' && !!config.period.start && !!config.period.end;

  const start = useCustomPeriod ? (config.period!.start as string) : period.start;
  const end = useCustomPeriod ? (config.period!.end as string) : period.end;

  // La dimension de regroupement (widget table) ne doit jamais restreindre la requête
  // au grain primaire : les totaux de la ligne parent restent ceux de toute la période,
  // indépendamment des valeurs masquées dans les sous-lignes.
  const rowGroupDimension = config.display?.row_group_dimension as string | undefined;
  const filters = (config.filters || []).filter((f) => !rowGroupDimension || f.field !== rowGroupDimension);

  const item: QueryItem = {
    id: widget.id,
    data_source_id: Number(config.data_source_id),
    metrics: config.metrics || [],
    dimensions: config.dimensions || [],
    filters,
    start,
    end,
  };

  // Comparaison :
  //  - période héritée du rapport + compare actif → dates de comparaison explicites (calculées par le PeriodPicker).
  //  - période custom du widget avec compare activé → le backend calcule la période précédente (compare_mode).
  //  - le widget peut désactiver la comparaison via config.compare === false.
  const reportCompare = !useCustomPeriod && period.compare && config.compare !== false;
  if (reportCompare && period.compare) {
    item.compare = true;
    item.compare_start = period.compare.start;
    item.compare_end = period.compare.end;
    item.compare_mode = period.compare.preset === 'previous_year' ? 'previous_year' : 'previous_period';
  } else if (config.compare === true) {
    item.compare = true;
    item.compare_mode = 'previous_period';
  }

  const granularity = (config.display?.granularity as string | undefined) ?? undefined;
  if (granularity !== undefined) item.granularity = granularity;

  const locationType = (config.display?.location_type as string | undefined) ?? undefined;
  if (locationType !== undefined && locationType !== 'all') item.location_type = locationType;

  return item;
}

/**
 * Item batch des sous-lignes d'un widget table configuré en regroupement hiérarchique
 * (`display.row_group_dimension`). `null` si non configuré ou aucune métrique cochée
 * "à segmenter" — inutile d'interroger le backend pour un jeu de colonnes vide.
 */
function buildRowGroupQueryItem(widget: Widget, period: ReportPeriod): QueryItem | null {
  if (widget.type !== 'table') return null;
  const config = widget.config;
  const rowGroupDimension = config.display?.row_group_dimension as string | undefined;
  const primaryDimension = config.dimensions?.[0];
  if (!rowGroupDimension || !primaryDimension) return null;

  const rowGroupMetrics = ((config.display?.row_group_metrics as string[] | undefined) ?? []).filter((m) =>
    (config.metrics || []).includes(m),
  );
  if (rowGroupMetrics.length === 0) return null;

  // Un filtre sur une métrique absente de `rowGroupMetrics` (donc absente du SELECT de
  // cette sous-requête) n'a pas de sens — et pire, l'API Ads le rejette explicitement en
  // WHERE clause dès qu'un segment de conversion est sélectionné, même pour des métriques
  // qui ne sont plus dans le SELECT (ex. `impressions > 0` casse une requête segmentée par
  // `conversion_action_name`, "unsupported metrics: impressions"). Les filtres de dimension
  // restent inchangés : eux ne posent pas ce problème de compatibilité.
  const filters = (config.filters || []).filter(
    (f) => f.target !== 'metric' || rowGroupMetrics.includes(f.field),
  );

  return {
    ...buildQueryItem(widget, period),
    id: `${widget.id}::rowgroup`,
    dimensions: [primaryDimension, rowGroupDimension],
    metrics: rowGroupMetrics,
    filters,
  };
}

export function useWidgetData(
  widgets: Widget[],
  period: ReportPeriod
): UseWidgetDataResult {
  const [dataByWidgetId, setDataByWidgetId] = useState<Record<string, WidgetQueryResult>>({});
  const [errorByWidgetId, setErrorByWidgetId] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const queryItems = useMemo(() => {
    const queryable = widgets.filter(isQueryableWidget);
    const base = queryable.map((w) => buildQueryItem(w, period));
    const rowGroups = queryable
      .map((w) => buildRowGroupQueryItem(w, period))
      .filter((item): item is QueryItem => item !== null);
    return [...base, ...rowGroups];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets, period]);

  // Clé stable dérivée du contenu (pas de la référence array) pour éviter
  // un re-fetch en boucle si le parent recrée `widgets`/`period` à chaque render.
  const stableKey = useMemo(() => JSON.stringify(queryItems), [queryItems]);

  useEffect(() => {
    const items = queryItems;

    if (items.length === 0) {
      setDataByWidgetId({});
      setErrorByWidgetId({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch('/api/query/batch', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ queries: items }),
    })
      .then(async (res) => {
        const json: BatchResponse = await res.json();
        if (!res.ok || json.success === false) {
          throw new Error('Erreur de requête batch');
        }
        return json;
      })
      .then((json) => {
        if (cancelled) return;

        const nextData: Record<string, WidgetQueryResult> = {};
        const nextErrors: Record<string, string> = {};

        for (const item of items) {
          if (item.id.endsWith('::rowgroup')) continue; // traité avec son widget parent ci-dessous
          const entry = json.data?.[item.id];
          if (!entry) continue;
          if (entry.error) {
            nextErrors[item.id] = entry.error.message;
          } else if (entry.data) {
            const rowGroupEntry = json.data?.[`${item.id}::rowgroup`];
            nextData[item.id] = {
              ...entry.data,
              subRows: rowGroupEntry?.data?.current ?? null,
              subRowsError: rowGroupEntry?.error?.message ?? null,
            };
          }
        }

        setDataByWidgetId(nextData);
        setErrorByWidgetId(nextErrors);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        // Échec réseau / requête entière : on marque tous les widgets concernés en erreur,
        // sans casser l'isolation individuelle qu'offrirait une réponse partielle du backend.
        const nextErrors: Record<string, string> = {};
        for (const item of items) {
          if (item.id.endsWith('::rowgroup')) continue; // pas d'entrée exposée pour l'id synthétique
          nextErrors[item.id] = err.message || 'Erreur réseau';
        }
        setDataByWidgetId({});
        setErrorByWidgetId(nextErrors);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  return { dataByWidgetId, loading, errorByWidgetId };
}
